import React, { ComponentType, ForwardRefRenderFunction, forwardRef, InputHTMLAttributes, ReactNode } from 'react'

import { TFunctionDetailedResult } from 'i18next'
import { NumberFormatValues, PatternFormat, PatternFormatProps, SourceInfo } from 'react-number-format'

import { AdornmentPosition } from '@/models/common'

import { Input } from './atoms'

interface OwnProps {
  hasError?: boolean
  onValueChange?: (values: NumberFormatValues, sourceInfo?: SourceInfo) => void
  error?: boolean
  adornment?: ReactNode | TFunctionDetailedResult
  adornmentPosition?: AdornmentPosition
  customInput?: ComponentType
  withIconRight?: boolean
  format?: string
}

type Props = OwnProps & Omit<PatternFormatProps, 'format' | 'customInput'>

type PFInputProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> & {
  type?: React.HTMLInputTypeAttribute
}

interface PatternFormatInputProps extends PFInputProps {
  adornment?: ReactNode
  adornmentPosition?: AdornmentPosition
}

const PatternFormatInput = forwardRef<HTMLInputElement, PatternFormatInputProps>(({ adornment, adornmentPosition, ...props }, ref) => (
  <Input ref={ref} {...props} adornment={adornment} adornmentPosition={adornmentPosition} type="text" />
))

const PatternInput: ForwardRefRenderFunction<Props> = (
  { hasError, onValueChange, id, value, customInput, format = '', withIconRight, adornment, adornmentPosition, ...rest }: Props,
  ref: any,
) => {
  const handleOnValueChange = (values: NumberFormatValues): void => {
    onValueChange && onValueChange(values)
  }

  const CustomInputComponent = customInput || PatternFormatInput

  return (
    <PatternFormat
      id={id}
      getInputRef={ref}
      error={hasError ? hasError : undefined}
      onValueChange={handleOnValueChange}
      value={value as string}
      autoComplete="off"
      customInput={CustomInputComponent}
      format={format}
      // Pass these as custom props that will be forwarded to the custom input
      {...{ adornment, adornmentPosition }}
      {...rest}
    />
  )
}

PatternInput.displayName = 'PatternInput'

export default forwardRef<any, Props>(PatternInput)
